export class UIProgressManager {
  constructor() {
    this.options = {};
    this.indeterminate = true;
    this.currentPercent = 0;

    // DOM elements
    this.overlay = null;
    this.bgLayer = null;
    this.progressContainer = null;
    this.progressBar = null;
    this.percentageText = null;
    this.loadingTextEl = null;
    this.retryButton = null;
  }

  /**
   * Initialize the progress manager.
   * @param {Object} options - Options to override built-in scheme values.
   */
  init(options = {}) {
    // Attempt to load saved scheme from localStorage.
    // If available, it overrides any options passed during init.
    const storedSchemeStr = localStorage.getItem("progressScheme");
    if (storedSchemeStr) {
      try {
        // Parse the stored scheme and override the passed options:
        options = JSON.parse(storedSchemeStr);
      } catch (e) {
      }
    }

    // Determine the built-in scheme options.
    let schemeOptions = {};
    if (typeof options.scheme === "string") {
      schemeOptions =
        UIProgressManager.builtinSchemes[options.scheme] ||
        UIProgressManager.builtinSchemes.light;
    } else {
      // If no scheme string available, choose based on system color mode.
      const isDarkMode =
        window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
      schemeOptions = isDarkMode
        ? UIProgressManager.builtinSchemes.dark
        : UIProgressManager.builtinSchemes.light;
    }

    // Merge the chosen built-in scheme with the provided (or stored) options.
    // Since stored options override, those values will take precedence.
    this.options = Object.assign({}, schemeOptions, options);

    // Create and insert the overlay, then show it with the entry animation.
    this.createOverlay();
    document.body.appendChild(this.overlay);
    this.show();
  }


  createOverlay() {
    // Create the full-screen overlay.
    this.overlay = document.createElement('div');
    this.overlay.className = 'ui-progress-overlay';

    // Create a background layer used for background animations.
    this.bgLayer = document.createElement('div');
    this.bgLayer.className = 'ui-progress-overlay-bg';
    this.bgLayer.style.backgroundColor =
      this.options.backgroundColor || this.options.overlayColor;
    // Only support gradient animation here.
    if (this.options.backgroundAnimation && this.options.backgroundAnimation === 'gradient') {
      this.bgLayer.classList.add('animate-bg-gradient');
    }
    this.overlay.appendChild(this.bgLayer);

    // Container that holds the progress UI (it appears above the background).
    this.progressContainer = document.createElement('div');
    this.progressContainer.className = 'ui-progress-container';

    // Create the progress bar element.
    if (this.options.progressBarType === 'circle') {
      this.progressBar = document.createElement('div');
      this.progressBar.className = 'ui-progress-circle indeterminate';

      // The inner fill is used in determinate mode.
      const circleFill = document.createElement('div');
      circleFill.className = 'circle-fill';
      this.progressBar.appendChild(circleFill);

      // Percentage text inside the circle.
      this.percentageText = document.createElement('span');
      this.percentageText.className = 'ui-progress-percentage';
      this.percentageText.textContent = '';
      if (!this.options.loadingTextColor || this.options.loadingTextColor.toLowerCase() === "auto") {
        this.percentageText.style.color = this.getContrastingColor(this.options.overlayColor || "#ffffff");
      } else {
        this.percentageText.style.color = this.options.loadingTextColor;
      }
      if (this.options.loadingTextFontSize) {
        this.percentageText.style.fontSize = this.options.loadingTextFontSize;
      }
      this.progressBar.appendChild(this.percentageText);
    } else {
      // For a rectangle progress bar, create a wrapper.
      const progressBarWrapper = document.createElement('div');
      progressBarWrapper.className = 'ui-progress-bar-wrapper';

      this.progressBar = document.createElement('div');
      // Start in indeterminate mode.
      this.progressBar.className = 'ui-progress-bar indeterminate';

      // Create the fill element.
      const progressFill = document.createElement('div');
      progressFill.className = 'ui-progress-fill';
      progressFill.style.backgroundColor = this.options.progressBarColor;
      this.progressBar.appendChild(progressFill);

      // Create percentage text.
      this.percentageText = document.createElement('span');
      this.percentageText.className = 'ui-progress-percentage';
      this.percentageText.textContent = '';
      this.progressBar.appendChild(this.percentageText);

      progressBarWrapper.appendChild(this.progressBar);
      // Use the wrapper as the main progress bar element.
      this.progressBar = progressBarWrapper;
    }

    // Create the loading text element.
    this.loadingTextEl = document.createElement('div');
    this.loadingTextEl.className = 'ui-loading-text';
    this.loadingTextEl.textContent = this.options.loadingText;
    // If loadingTextColor is "auto" or not set, calculate based on overlayColor.
    if (!this.options.loadingTextColor || this.options.loadingTextColor.toLowerCase() === "auto") {
      this.loadingTextEl.style.color = this.getContrastingColor(this.options.overlayColor || "#ffffff");
    } else {
      this.loadingTextEl.style.color = this.options.loadingTextColor;
    }
    // Apply font size if provided.
    if (this.options.loadingTextFontSize) {
      this.loadingTextEl.style.fontSize = this.options.loadingTextFontSize;
    }


    this.progressContainer.appendChild(this.progressBar);
    this.progressContainer.appendChild(this.loadingTextEl);
    this.overlay.appendChild(this.progressContainer);

    // Inject CSS with new keyframes and class definitions.
    this.injectStyles();
  }

  injectStyles() {
    // Use the animation classes specified in options.
    const entryClass = this.options.entryAnimationClass || 'entry-default';
    const exitClass = this.options.exitAnimationClass || 'exit-default';

    const style = document.createElement('style');
    style.type = 'text/css';
    style.innerHTML = `
      /* Overlay base styles */
      .ui-progress-overlay {
        position: fixed;
        top: 0; left: 0;
        width: 100%; height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        opacity: 0;
        z-index: 9999;
      }
      .ui-progress-overlay-bg {
        position: absolute;
        top: 0; left: 0;
        width: 100%; height: 100%;
        z-index: 1;
      }
      .ui-progress-container {
        position: relative;
        z-index: 2;
        text-align: center;
      }

      /* Entry animations */
      .entry-default {
        animation: entryDefault 0.7s forwards;
      }
      @keyframes entryDefault {
        0% { opacity: 0; transform: scale(0.95); }
        100% { opacity: 1; transform: scale(1); }
      }
      .fade-in {
        animation: fadeIn 0.7s forwards;
      }
      @keyframes fadeIn {
        0% { opacity: 0; }
        100% { opacity: 1; }
      }
      .slide-in {
        animation: slideIn 0.7s forwards;
      }
      @keyframes slideIn {
        0% { opacity: 0; transform: translateY(-30px); }
        100% { opacity: 1; transform: translateY(0); }
      }
      
      /* Exit animations */
      .exit-default {
        animation: exitDefault 0.8s forwards;
      }
      @keyframes exitDefault {
        0% { opacity: 1; transform: scale(1); }
        100% { opacity: 0; transform: scale(0.85) translateY(20px); }
      }

      .fade-out {
        animation: fadeOut 0.8s forwards;
      }
      @keyframes fadeOut {
        0% { opacity: 1; }
        100% { opacity: 0; transform: translateY(10px); }
      }

      .slide-out {
        animation: slideOut 0.8s forwards;
      }
      @keyframes slideOut {
        0% { opacity: 1; transform: translateY(0); }
        100% { opacity: 0; transform: translateY(30px); }
      }


      /* Background Animation – only Gradient is supported */
      @keyframes gradientAnimation {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
      .animate-bg-gradient {
        background: linear-gradient(270deg, ${this.options.overlayColor || '#ffffff'}, ${this.options.backgroundColor || '#f4f4f4'});
        background-size: 200% 200%;
        animation: gradientAnimation 8s ease infinite;
      }

      @keyframes pulseAnimation {
            0% { filter: brightness(1); }
            50% { filter: brightness(1.03); }
            100% { filter: brightness(1); }
      }
      .animate-bg-pulse {
        animation: pulseAnimation 4s ease-in-out infinite;
      }

      /* Rectangle progress bar styles */
      .ui-progress-bar-wrapper {
        width: 300px;
        height: 30px;
        background: #e0e0e0;
        border-radius: 15px;
        overflow: hidden;
        position: relative;
        margin-bottom: 10px;
      }
      .ui-progress-bar {
        width: 100%;
        height: 100%;
        position: relative;
      }
      .ui-progress-fill {
        height: 100%;
        width: 0%;
        transition: width 0.3s;
        background-color: ${this.options.progressBarColor || '#007bff'};
      }
      /* Indeterminate mode for rectangle using shimmer effect */
      @keyframes shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
      .ui-progress-bar.indeterminate .ui-progress-fill {
        width: 100%;
        background: linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent);
        background-size: 200% 100%;
        animation: shimmer 1.5s infinite;
      }
      .ui-progress-percentage {
        position: absolute;
        top: 0; left: 0;
        width: 100%;
        height: 100%;
        line-height: 30px;
        text-align: center;
        font-weight: bold;
        color: #fff;
        pointer-events: none;
      }
      .ui-loading-text {
        margin-top: 10px;
      }

      /* Circle progress bar styles */
      .ui-progress-circle {
        width: 100px;
        height: 100px;
        border-radius: 50%;
        border: 8px solid #e0e0e0;
        position: relative;
        margin-bottom: 10px;
        display: flex;
        justify-content: center;
        align-items: center;
      }
      .ui-progress-circle .circle-fill {
        position: absolute;
        top: 0; left: 0;
        width: 100%;
        height: 100%;
        border-radius: 50%;
        clip: rect(0px, 100px, 100px, 50px);
        background-color: ${this.options.progressBarColor || '#007bff'};
        transition: transform 0.3s;
      }
      .ui-progress-circle .ui-progress-percentage {
        position: relative;
        font-weight: bold;
        color: #fff;
      }
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      .ui-progress-circle.indeterminate .circle-fill {
        animation: spin 1.5s linear infinite;
      }

      /* Wave Animation for determinate rectangle progress fill */
      @keyframes waveAnimation {
        0% { background-position: 0% 0; }
        100% { background-position: 100% 0; }
      }
      .ui-progress-wave {
        background: linear-gradient(90deg, ${this.options.progressBarColor || '#007bff'}, #ffffff, ${this.options.progressBarColor || '#007bff'});
        background-size: 200% 100%;
        animation: waveAnimation 2s linear infinite;
      }
    `;
    document.head.appendChild(style);
  }

  show() {
    document.body.style.overflow = 'hidden';
    // Reveal body now that the overlay is covering it.
    // Apps that set body{visibility:hidden} in their CSS avoid any
    // login-page flash because nothing is visible until this point.
    document.body.style.visibility = 'visible';
    // Apply the entry animation class.
    const entryClass = this.options.entryAnimationClass || 'entry-default';
    this.overlay.classList.add(entryClass);
  }

  hide() {
    document.body.style.overflow = '';
    // Remove the entry class and add the exit animation class.
    const exitClass = this.options.exitAnimationClass || 'exit-default';
    this.overlay.classList.remove(this.options.entryAnimationClass || 'entry-default');
    this.overlay.classList.add(exitClass);
    // Remove the overlay from the DOM after a delay so the exit animation can be seen.
    setTimeout(() => {
      if (this.overlay && this.overlay.parentNode) {
        this.overlay.parentNode.removeChild(this.overlay);
      }
    }, 1000); // Delay (in ms) adjusted to allow the animation to play.
  }

  reset() {
    this.indeterminate = true;
    this.currentPercent = 0;
    this.updateProgressDisplay(0);
    if (this.options.progressBarType === 'circle') {
      this.progressBar.classList.add('indeterminate');
    } else {
      this.progressBar.querySelector('.ui-progress-bar').classList.add('indeterminate');
    }
  }

  /**
   * Switch to determinate mode and update progress.
   * @param {string} text - Loading text.
   * @param {number} percent - Progress percentage.
   */
  setProgress(text, percent) {
    if (text) {
      this.loadingTextEl.textContent = text;
    }
    if (percent > 0) {
      this.indeterminate = false;
      this.currentPercent = percent;
      if (this.options.progressBarType === 'circle') {
        this.progressBar.classList.remove('indeterminate');
      } else {
        this.progressBar.querySelector('.ui-progress-bar').classList.remove('indeterminate');
      }
      this.updateProgressDisplay(percent);
    }
  }

  updateProgressDisplay(percent) {
    if (!this.indeterminate) {
      this.percentageText.textContent = `${percent}%`;
    }
    if (this.options.progressBarType === 'rectangle') {
      let fill = this.progressBar.querySelector('.ui-progress-fill');
      if (fill && !this.indeterminate) {
        fill.style.width = `${percent}%`;
        // Apply “wave” animation if requested; otherwise use the plain fill.
        if (this.options.progressAnimation === 'wave') {
          fill.classList.add('ui-progress-wave');
        } else {
          fill.classList.remove('ui-progress-wave');
        }
      }
    } else if (this.options.progressBarType === 'circle') {
      const circleFill = this.progressBar.querySelector('.circle-fill');
      if (circleFill && !this.indeterminate) {
        const rotation = (360 * percent) / 100;
        circleFill.style.transform = `rotate(${rotation}deg)`;
      }
    }
  }

  setErrorMode() {
    this.progressContainer.innerHTML = '';
    const errorMessage = document.createElement('div');
    errorMessage.className = 'ui-error-message';
    errorMessage.textContent = 'An error occurred.';
    this.progressContainer.appendChild(errorMessage);
    this.retryButton = document.createElement('button');
    this.retryButton.className = 'ui-retry-button';
    this.retryButton.textContent = 'Retry';
    this.retryButton.addEventListener('click', () => window.location.reload());
    this.progressContainer.appendChild(this.retryButton);
  }

  getContrastingColor(hex) {
    // Remove '#' if present
    hex = hex.replace('#', '');
    // Parse red, green and blue values
    let r = parseInt(hex.substr(0, 2), 16);
    let g = parseInt(hex.substr(2, 2), 16);
    let b = parseInt(hex.substr(4, 2), 16);
    // Calculate luminance (YIQ formula)
    let yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    return (yiq >= 128) ? "#000000" : "#ffffff";
  }

}

// Built-in schemes with sensible defaults.
UIProgressManager.builtinSchemes = {
  light: {
    overlayColor: '#FFFFFF',
    backgroundColor: '#FFFFFF',
    loadingText: 'Loading...',
    loadingTextColor: "auto",
    loadingTextFontSize: "22px",
    progressBarType: 'rectangle',
    progressBarColor: '#007bff',
    backgroundAnimation: 'gradient',
    progressAnimation: 'wave',
    entryAnimationClass: 'slide-in',
    exitAnimationClass: 'slide-out'
  },
  dark: {
    overlayColor: 'rgba(0,0,0,0.9)',
    backgroundColor: '#333333',
    loadingText: 'Loading...',
    loadingTextColor: "auto",
    loadingTextFontSize: "22px",
    progressBarType: 'rectangle',
    progressBarColor: '#66afe9',
    backgroundAnimation: 'gradient',
    progressAnimation: 'wave',
    entryAnimationClass: 'slide-in',
    exitAnimationClass: 'slide-out'
  },
  animatedBlueScheme: {
    overlayColor: 'rgba(0,0,50,0.9)',
    backgroundColor: '#001133',
    loadingText: 'Loading...',
    progressBarType: 'rectangle',
    progressBarColor: '#1E90FF',
    backgroundAnimation: 'gradient',
    progressAnimation: 'none',
    entryAnimationClass: 'entry-default',
    exitAnimationClass: 'exit-default'
  },
  vibrantPulse: {
    overlayColor: 'rgba(255,0,0,0.8)',
    backgroundColor: '#330000',
    loadingText: 'Please wait...',
    progressBarType: 'circle',
    progressBarColor: '#ff6666',
    backgroundAnimation: 'none',
    progressAnimation: 'none',
    entryAnimationClass: 'entry-default',
    exitAnimationClass: 'exit-default'
  }
};
